/**
 * Lista Exprés — wizard comercial (POS-PRICE-LISTS-001B).
 * Extiende Precios y Promociones; precios desde motor existente (no SSOT paralelo).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FaArrowLeft,
  FaArrowRight,
  FaCopy,
  FaDownload,
  FaSave,
  FaShareAlt,
  FaWhatsapp,
  FaArchive,
  FaClone
} from 'react-icons/fa';

import Button from '../../common/Button';
import Card from '../../common/Card';
import Spinner from '../../common/Spinner';
import ListaExpresPreview from './ListaExpresPreview';
import ListaPreciosImprimir from './ListaPreciosImprimir';

import listasExpresService from '../../../services/listas-expres.service';
import { etiquetaLista, LISTA_PRECIO_KEYS } from '../../../utils/listasPreciosLabels';
import { uniqueMarcas } from '../../../utils/catalogoComercialExport';
import { formatCurrency } from '../../../utils/format';
import {
  DEFAULT_VISIBLE_FIELDS,
  LISTAS_EXPRES_FORMATS,
  LISTAS_EXPRES_TEMPLATES,
  buildWhatsAppText,
  getTemplate,
  orderProducts,
  resolveExpresPrice
} from '../../../utils/listasExpresCatalog';
import { exportNodeAsImage, shareImageIfPossible } from '../../../utils/listasExpresExport';

const STEPS = [
  { id: 'productos', label: 'Productos' },
  { id: 'lista', label: 'Lista' },
  { id: 'plantilla', label: 'Plantilla' },
  { id: 'personalizar', label: 'Personalizar' },
  { id: 'preview', label: 'Preview' }
];

function stockOf(p) {
  const n = Number(p?.stock_actual ?? p?.stock ?? 0);
  return Number.isFinite(n) ? n : 0;
}

const emptyDraft = () => ({
  id: null,
  name: '',
  description: '',
  productIds: [],
  productOrder: [],
  priceListKey: 'mayorista',
  templateKey: 'lista_limpia',
  formatKey: 'a4_vertical',
  visibleFields: { ...DEFAULT_VISIBLE_FIELDS },
  highlightedProductIds: [],
  hidePriceIds: [],
  commercialTitle: 'Lista de precios',
  commercialSubtitle: '',
  commercialConditions: '',
  cta: 'Consultanos',
  leyenda: '',
  validFrom: '',
  validUntil: '',
  backgroundKey: 'slate',
  status: 'draft'
});

const ListaExpresPanel = ({
  productos = [],
  categorias = [],
  proveedores = [],
  empresaConfig = null,
  onClose
}) => {
  const previewRef = useRef(null);
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState(emptyDraft);
  const [savedList, setSavedList] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [waText, setWaText] = useState('');
  const [mostrarPdf, setMostrarPdf] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [proveedorId, setProveedorId] = useState('');
  const [marca, setMarca] = useState('');
  const [stockFilter, setStockFilter] = useState('todos'); // todos|con|sin
  const [selected, setSelected] = useState(() => new Set());

  const marcas = useMemo(() => uniqueMarcas(productos), [productos]);

  useEffect(() => {
    cargarGuardadas();
  }, []);

  useEffect(() => {
    setDraft((d) => ({
      ...d,
      productIds: Array.from(selected),
      productOrder: (() => {
        const ids = Array.from(selected);
        const prev = (d.productOrder || []).filter((id) => selected.has(id));
        ids.forEach((id) => {
          if (!prev.includes(id)) prev.push(id);
        });
        return prev;
      })()
    }));
  }, [selected]);

  const filtrados = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return productos.filter((p) => {
      const okTerm =
        !term ||
        String(p.nombre || '')
          .toLowerCase()
          .includes(term) ||
        String(p.codigo || '')
          .toLowerCase()
          .includes(term);
      const okCat = !categoriaId || p.categoria_id === categoriaId;
      const m = String(p.marca || p.marca_nombre || '').trim();
      const okMarca = !marca || m === marca;
      const okProv = !proveedorId || String(p.proveedor_id || '') === String(proveedorId);
      const st = stockOf(p);
      const okStock =
        stockFilter === 'todos' ||
        (stockFilter === 'con' && st > 0) ||
        (stockFilter === 'sin' && st <= 0);
      return okTerm && okCat && okMarca && okProv && okStock;
    });
  }, [productos, searchTerm, categoriaId, proveedorId, marca, stockFilter]);

  const productosDraft = useMemo(() => {
    const map = new Map(productos.map((p) => [p.id, p]));
    return orderProducts(
      (draft.productOrder || []).map((id) => map.get(id)).filter(Boolean),
      draft.productOrder
    );
  }, [productos, draft.productOrder]);

  const template = getTemplate(draft.templateKey);

  const cargarGuardadas = async () => {
    try {
      setLoadingSaved(true);
      const items = await listasExpresService.listar();
      setSavedList(Array.isArray(items) ? items : []);
    } catch (e) {
      console.error(e);
      setSavedList([]);
    } finally {
      setLoadingSaved(false);
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filtrados.forEach((p) => next.add(p.id));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const moveOrder = (id, dir) => {
    setDraft((d) => {
      const arr = [...(d.productOrder || [])];
      const i = arr.indexOf(id);
      if (i < 0) return d;
      const j = dir === 'up' ? i - 1 : i + 1;
      if (j < 0 || j >= arr.length) return d;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...d, productOrder: arr };
    });
  };

  const toggleHighlight = (id) => {
    setDraft((d) => {
      const set = new Set(d.highlightedProductIds || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...d, highlightedProductIds: Array.from(set) };
    });
  };

  const toggleHidePrice = (id) => {
    setDraft((d) => {
      const set = new Set(d.hidePriceIds || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...d, hidePriceIds: Array.from(set) };
    });
  };

  const applySaved = (item) => {
    const ids = item.productIds || [];
    setSelected(new Set(ids));
    setDraft({
      ...emptyDraft(),
      id: item.id,
      name: item.name || '',
      description: item.description || '',
      productIds: ids,
      productOrder: item.productOrder || ids,
      priceListKey: item.priceListKey || 'mayorista',
      templateKey: item.templateKey || 'lista_limpia',
      formatKey: item.formatKey || 'a4_vertical',
      visibleFields: { ...DEFAULT_VISIBLE_FIELDS, ...(item.visibleFields || {}) },
      highlightedProductIds: item.highlightedProductIds || [],
      hidePriceIds: item.hidePriceIds || [],
      commercialTitle: item.commercialTitle || 'Lista de precios',
      commercialSubtitle: item.commercialSubtitle || '',
      commercialConditions: item.commercialConditions || '',
      cta: item.cta || 'Consultanos',
      leyenda: item.leyenda || '',
      validFrom: item.validFrom || '',
      validUntil: item.validUntil || '',
      status: item.status || 'draft',
      backgroundKey: item.backgroundKey || 'slate'
    });
    setStep(1);
    toast.info('Lista Exprés reabierta · precios actualizados del catálogo');
  };

  const buildPayload = () => ({
    name: draft.name || commercialFallbackName(),
    description: draft.description,
    productIds: draft.productOrder,
    productOrder: draft.productOrder,
    priceListKey: draft.priceListKey,
    templateKey: draft.templateKey,
    formatKey: draft.formatKey,
    visibleFields: draft.visibleFields,
    highlightedProductIds: draft.highlightedProductIds,
    hidePriceIds: draft.hidePriceIds || [],
    commercialTitle: draft.commercialTitle,
    commercialSubtitle: draft.commercialSubtitle,
    commercialConditions: draft.commercialConditions,
    cta: draft.cta,
    leyenda: draft.leyenda,
    validFrom: draft.validFrom || null,
    validUntil: draft.validUntil || null,
    status: draft.status || 'draft',
    backgroundKey: draft.backgroundKey
  });

  const commercialFallbackName = () =>
    draft.commercialTitle?.trim() || `Lista Exprés ${new Date().toLocaleDateString('es-AR')}`;

  const guardar = async () => {
    if (!draft.productOrder.length) {
      toast.warning('Seleccioná al menos un producto');
      return;
    }
    try {
      setSaving(true);
      const payload = buildPayload();
      let saved;
      if (draft.id) {
        saved = await listasExpresService.actualizar(draft.id, payload);
      } else {
        saved = await listasExpresService.crear(payload);
      }
      const id = saved?.id || draft.id;
      setDraft((d) => ({ ...d, id, name: payload.name }));
      toast.success('Lista Exprés guardada');
      await cargarGuardadas();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const duplicar = async () => {
    if (!draft.id) {
      toast.warning('Guardá la lista antes de duplicar');
      return;
    }
    try {
      const copy = await listasExpresService.duplicar(draft.id);
      toast.success('Duplicada');
      if (copy?.id) applySaved(copy);
      await cargarGuardadas();
    } catch (e) {
      toast.error('No se pudo duplicar');
    }
  };

  const archivar = async () => {
    if (!draft.id) return;
    try {
      await listasExpresService.archivar(draft.id);
      toast.success('Archivada');
      setDraft(emptyDraft());
      setSelected(new Set());
      await cargarGuardadas();
    } catch (e) {
      toast.error('No se pudo archivar');
    }
  };

  const exportImg = async (format) => {
    try {
      await exportNodeAsImage(previewRef.current, {
        format,
        filename: `lista-expres-${draft.formatKey || 'preview'}`
      });
      toast.success(`${format.toUpperCase()} descargado`);
    } catch (e) {
      console.error(e);
      toast.error('Error al exportar imagen');
    }
  };

  const compartir = async () => {
    const text = buildWhatsAppText({
      empresaConfig,
      commercialTitle: draft.commercialTitle,
      commercialSubtitle: draft.commercialSubtitle,
      validUntil: draft.validUntil,
      productos: productosDraft,
      priceListKey: draft.priceListKey,
      highlightedProductIds: draft.highlightedProductIds,
      cta: draft.cta
    });
    setWaText(text);
    const share = await shareImageIfPossible(previewRef.current, {
      title: draft.commercialTitle || 'Lista comercial',
      text
    });
    if (!share.ok) {
      toast.info('Descargá la imagen y pegá el texto en WhatsApp');
    }
  };

  const copiarTexto = async () => {
    const text =
      waText ||
      buildWhatsAppText({
        empresaConfig,
        commercialTitle: draft.commercialTitle,
        commercialSubtitle: draft.commercialSubtitle,
        validUntil: draft.validUntil,
        productos: productosDraft,
        priceListKey: draft.priceListKey,
        highlightedProductIds: draft.highlightedProductIds,
        cta: draft.cta
      });
    setWaText(text);
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Texto copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const canNext = () => {
    if (step === 0) return selected.size > 0;
    return true;
  };

  const setVf = (key, value) => {
    setDraft((d) => ({
      ...d,
      visibleFields: { ...d.visibleFields, [key]: value }
    }));
  };

  return (
    <div className="space-y-4" data-testid="lista-expres-panel">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-e0e3-foreground">Lista Exprés</h2>
          <p className="text-sm text-e0e3-muted">
            Material comercial en minutos · precios desde el catálogo actual
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button color="secondary" icon={<FaSave />} onClick={guardar} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
          <Button color="secondary" icon={<FaClone />} onClick={duplicar} disabled={!draft.id}>
            Duplicar
          </Button>
          <Button color="secondary" icon={<FaArchive />} onClick={archivar} disabled={!draft.id}>
            Archivar
          </Button>
          {onClose ? (
            <Button color="secondary" icon={<FaArrowLeft />} onClick={onClose}>
              Volver
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {STEPS.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setStep(i)}
            className={`px-3 py-2 rounded-lg text-sm min-h-[40px] border ${
              i === step
                ? 'border-e0e3-accent bg-e0e3-accent/15 text-e0e3-foreground'
                : 'border-white/10 text-e0e3-muted'
            }`}
          >
            {i + 1}. {s.label}
          </button>
        ))}
      </div>

      {step === 0 && (
        <Card>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              <input
                className="nexo-field"
                placeholder="Buscar nombre o código"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <select
                className="nexo-field"
                value={categoriaId}
                onChange={(e) => setCategoriaId(e.target.value)}
              >
                <option value="">Todas las categorías</option>
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
              <select
                className="nexo-field"
                value={proveedorId}
                onChange={(e) => setProveedorId(e.target.value)}
              >
                <option value="">Todos los proveedores</option>
                {proveedores.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre || p.razon_social}
                  </option>
                ))}
              </select>
              <select className="nexo-field" value={marca} onChange={(e) => setMarca(e.target.value)}>
                <option value="">Todas las marcas</option>
                {marcas.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <select
                className="nexo-field"
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
              >
                <option value="todos">Stock: todos</option>
                <option value="con">Con stock</option>
                <option value="sin">Sin stock</option>
              </select>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button color="secondary" onClick={selectAllFiltered}>
                Seleccionar filtrados ({filtrados.length})
              </Button>
              <Button color="secondary" onClick={clearSelection}>
                Deseleccionar todos
              </Button>
              <span className="text-sm text-e0e3-muted self-center">
                {selected.size} seleccionados
              </span>
            </div>
            <div className="max-h-[420px] overflow-auto border border-white/10 rounded-lg divide-y divide-white/5">
              {filtrados.slice(0, 300).map((p) => (
                <label
                  key={p.id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-white/[0.03] cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggleOne(p.id)}
                    className="w-5 h-5"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-e0e3-foreground truncate">{p.nombre}</div>
                    <div className="text-xs text-e0e3-muted">
                      {p.codigo || '—'} · stock {stockOf(p)}
                    </div>
                  </div>
                  <div className="text-sm text-e0e3-accent-soft">
                    {formatCurrency(resolveExpresPrice(p, draft.priceListKey))}
                  </div>
                </label>
              ))}
              {!filtrados.length ? (
                <p className="p-4 text-e0e3-muted text-sm">No hay productos con estos filtros.</p>
              ) : null}
            </div>

            {loadingSaved ? (
              <Spinner />
            ) : (
              <div data-testid="lista-expres-guardadas">
                <h3 className="text-sm font-medium text-e0e3-foreground mb-2">Guardadas</h3>
                {savedList.filter((x) => x.status !== 'archived').length ? (
                  <div className="flex flex-wrap gap-2">
                    {savedList
                      .filter((x) => x.status !== 'archived')
                      .slice(0, 12)
                      .map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          data-testid={`lista-expres-saved-${item.id}`}
                          onClick={() => applySaved(item)}
                          className="px-3 py-2 rounded border border-white/10 text-sm hover:border-e0e3-accent/50"
                        >
                          {item.name || 'Sin nombre'}
                        </button>
                      ))}
                  </div>
                ) : (
                  <p className="text-xs text-e0e3-muted">Aún no hay listas activas guardadas.</p>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {step === 1 && (
        <Card>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-e0e3-muted mb-1">Lista de precios</label>
              <select
                className="nexo-field max-w-md"
                value={draft.priceListKey}
                onChange={(e) => setDraft((d) => ({ ...d, priceListKey: e.target.value }))}
              >
                {LISTA_PRECIO_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {etiquetaLista(empresaConfig, k)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-e0e3-muted mt-2">
                Los precios se resuelven al momento · no se guardan en la Lista Exprés
              </p>
            </div>
            <div className="max-h-[360px] overflow-auto space-y-2">
              {productosDraft.map((p, idx) => (
                <div
                  key={p.id}
                  className="flex flex-wrap items-center gap-2 border border-white/10 rounded-lg px-3 py-2"
                >
                  <span className="text-xs text-e0e3-muted w-6">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{p.nombre}</div>
                    <div className="text-sm text-e0e3-accent-soft">
                      {formatCurrency(resolveExpresPrice(p, draft.priceListKey))}
                    </div>
                  </div>
                  <button type="button" className="text-xs underline" onClick={() => moveOrder(p.id, 'up')}>
                    Subir
                  </button>
                  <button type="button" className="text-xs underline" onClick={() => moveOrder(p.id, 'down')}>
                    Bajar
                  </button>
                  <button type="button" className="text-xs underline" onClick={() => toggleHighlight(p.id)}>
                    {(draft.highlightedProductIds || []).includes(p.id) ? 'Quitar destaque' : 'Destacar'}
                  </button>
                  <button type="button" className="text-xs underline" onClick={() => toggleHidePrice(p.id)}>
                    {(draft.hidePriceIds || []).includes(p.id) ? 'Mostrar precio' : 'Ocultar precio'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2">Plantilla</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                {LISTAS_EXPRES_TEMPLATES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => {
                      const next = { ...draft, templateKey: t.key };
                      if (t.key === 'wa_vertical' || t.key === 'ig_historia') next.formatKey = 'wa_vertical';
                      if (t.key === 'wa_cuadrado' || t.key === 'ig_post') next.formatKey = 'wa_cuadrado';
                      if (t.key.startsWith('flyer') || t.group === 'lista' || t.group === 'catalogo') {
                        if (!LISTAS_EXPRES_FORMATS.find((f) => f.key === next.formatKey)?.kind) {
                          next.formatKey = 'a4_vertical';
                        }
                      }
                      setDraft(next);
                    }}
                    className={`text-left p-3 rounded-lg border ${
                      draft.templateKey === t.key
                        ? 'border-e0e3-accent bg-e0e3-accent/10'
                        : 'border-white/10'
                    }`}
                  >
                    <div className="font-medium">{t.label}</div>
                    <div className="text-xs text-e0e3-muted">Hasta {t.maxProducts} productos</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-medium mb-2">Formato</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
                {LISTAS_EXPRES_FORMATS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, formatKey: f.key }))}
                    className={`text-left p-3 rounded-lg border ${
                      draft.formatKey === f.key
                        ? 'border-e0e3-accent bg-e0e3-accent/10'
                        : 'border-white/10'
                    }`}
                  >
                    <div className="font-medium text-sm">{f.label}</div>
                    <div className="text-xs text-e0e3-muted">
                      {f.width}×{f.height} · {f.kind}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-e0e3-muted">
              Plantilla actual: {template.label} · máx. visibles {template.maxProducts}
            </p>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <input
                className="nexo-field"
                placeholder="Nombre al guardar"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              />
              <input
                className="nexo-field"
                placeholder="Título comercial"
                value={draft.commercialTitle}
                onChange={(e) => setDraft((d) => ({ ...d, commercialTitle: e.target.value }))}
              />
              <input
                className="nexo-field"
                placeholder="Subtítulo"
                value={draft.commercialSubtitle}
                onChange={(e) => setDraft((d) => ({ ...d, commercialSubtitle: e.target.value }))}
              />
              <input
                className="nexo-field"
                placeholder="Vigencia hasta (texto)"
                value={draft.validUntil}
                onChange={(e) => setDraft((d) => ({ ...d, validUntil: e.target.value }))}
              />
              <input
                className="nexo-field"
                placeholder="Llamada a la acción"
                value={draft.cta}
                onChange={(e) => setDraft((d) => ({ ...d, cta: e.target.value }))}
              />
              <input
                className="nexo-field"
                placeholder="Leyenda comercial"
                value={draft.leyenda}
                onChange={(e) => setDraft((d) => ({ ...d, leyenda: e.target.value }))}
              />
              <textarea
                className="nexo-field"
                rows={2}
                placeholder="Condiciones"
                value={draft.commercialConditions}
                onChange={(e) => setDraft((d) => ({ ...d, commercialConditions: e.target.value }))}
              />
              <select
                className="nexo-field"
                value={draft.backgroundKey}
                onChange={(e) => setDraft((d) => ({ ...d, backgroundKey: e.target.value }))}
              >
                <option value="slate">Fondo slate</option>
                <option value="forest">Fondo forest</option>
                <option value="wine">Fondo wine</option>
                <option value="sand">Fondo sand</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2 content-start">
              {Object.keys(DEFAULT_VISIBLE_FIELDS).map((key) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.visibleFields?.[key] !== false}
                    onChange={(e) => setVf(key, e.target.checked)}
                  />
                  {key.replace(/_/g, ' ')}
                </label>
              ))}
            </div>
          </div>
        </Card>
      )}

      {step === 4 && (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
          <Card>
            <ListaExpresPreview
              ref={previewRef}
              productos={productosDraft}
              productOrder={draft.productOrder}
              priceListKey={draft.priceListKey}
              templateKey={draft.templateKey}
              formatKey={draft.formatKey}
              visibleFields={draft.visibleFields}
              highlightedProductIds={draft.highlightedProductIds}
              hidePriceIds={draft.hidePriceIds}
              commercialTitle={draft.commercialTitle}
              commercialSubtitle={draft.commercialSubtitle}
              validUntil={draft.validUntil}
              commercialConditions={draft.commercialConditions}
              cta={draft.cta}
              leyenda={draft.leyenda}
              empresaConfig={empresaConfig}
              backgroundKey={draft.backgroundKey}
            />
          </Card>
          <Card>
            <div className="space-y-2">
              <Button color="primary" icon={<FaDownload />} onClick={() => exportImg('png')} className="w-full">
                Descargar PNG
              </Button>
              <Button color="secondary" icon={<FaDownload />} onClick={() => exportImg('jpg')} className="w-full">
                Descargar JPG
              </Button>
              <Button
                color="secondary"
                icon={<FaWhatsapp />}
                onClick={compartir}
                className="w-full"
              >
                Compartir / WhatsApp
              </Button>
              <Button color="secondary" icon={<FaCopy />} onClick={copiarTexto} className="w-full">
                Copiar texto WhatsApp
              </Button>
              <Button
                color="secondary"
                icon={<FaShareAlt />}
                onClick={() => setMostrarPdf(true)}
                className="w-full"
              >
                Vista PDF (Sprint A)
              </Button>
              <Button color="primary" icon={<FaSave />} onClick={guardar} disabled={saving} className="w-full">
                Guardar configuración
              </Button>
              {waText ? (
                <textarea
                  className="nexo-field text-xs"
                  rows={8}
                  value={waText}
                  onChange={(e) => setWaText(e.target.value)}
                />
              ) : null}
            </div>
          </Card>
        </div>
      )}

      <div className="flex justify-between">
        <Button
          color="secondary"
          icon={<FaArrowLeft />}
          disabled={step === 0}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
        >
          Anterior
        </Button>
        <Button
          color="primary"
          icon={<FaArrowRight />}
          disabled={step === STEPS.length - 1 || !canNext()}
          onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
        >
          Siguiente
        </Button>
      </div>

      {mostrarPdf ? (
        <ListaPreciosImprimir
          productos={productosDraft}
          categorias={categorias}
          listaInicial={draft.priceListKey}
          onClose={() => setMostrarPdf(false)}
        />
      ) : null}
    </div>
  );
};

export default ListaExpresPanel;
